import React, { useState } from 'react';
import logo from "../reactnest.png";
import { Link } from "react-router-dom";

const ContributePage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const contributionTypes = [
    {
      icon: '💻',
      title: 'Code Contributions',
      description: 'Help build new features, fix bugs, and improve performance',
      skills: ['JavaScript', 'React', 'Node.js', 'TypeScript'],
      difficulty: 'Intermediate'
    },
    {
      icon: '📝',
      title: 'Documentation',
      description: 'Write guides, tutorials, and improve existing documentation',
      skills: ['Technical Writing', 'Markdown', 'Git'],
      difficulty: 'Beginner'
    },
    {
      icon: '🎨',
      title: 'UI/UX Design',
      description: 'Design interfaces, create mockups, and enhance user experience',
      skills: ['Figma', 'Design Systems', 'Prototyping'],
      difficulty: 'Intermediate'
    },
    {
      icon: '🐛',
      title: 'Bug Reports & Testing',
      description: 'Test features, report bugs, and help maintain quality',
      skills: ['QA Testing', 'Bug Reporting', 'User Testing'],
      difficulty: 'Beginner'
    },
    {
      icon: '🌍',
      title: 'Translation',
      description: 'Help make React Nest accessible to developers worldwide',
      skills: ['Language Skills', 'Localization', 'Cultural Awareness'],
      difficulty: 'Beginner'
    },
    {
      icon: '📢',
      title: 'Community Support',
      description: 'Help other developers, answer questions, and moderate discussions',
      skills: ['Communication', 'React Knowledge', 'Community Management'],
      difficulty: 'Beginner'
    }
  ];

  const gettingStartedSteps = [
    {
      step: '01',
      title: 'Fork the Repository',
      description: 'Create your own copy of React Nest to work on',
      command: 'git clone https://github.com/yourusername/react-nest.git'
    },
    {
      step: '02',
      title: 'Set Up Development Environment',
      description: 'Install dependencies and configure your local environment',
      command: 'npm install && npm run dev'
    },
    {
      step: '03',
      title: 'Find an Issue',
      description: 'Browse open issues and pick one that matches your skills',
      command: 'Look for "good first issue" or "help wanted" labels'
    },
    {
      step: '04',
      title: 'Create a Branch',
      description: 'Create a new branch for your contribution',
      command: 'git checkout -b feature/your-feature-name'
    },
    {
      step: '05',
      title: 'Make Your Changes',
      description: 'Implement your feature or fix following our coding standards',
      command: 'Follow the coding guidelines in CONTRIBUTING.md'
    },
    {
      step: '06',
      title: 'Submit Pull Request',
      description: 'Submit your changes for review by the community',
      command: 'git push origin feature/your-feature-name'
    }
  ];

  const communityGuidelines = [
    {
      icon: '🤝',
      title: 'Be Respectful',
      description: 'Treat all community members with respect and kindness'
    },
    {
      icon: '💡',
      title: 'Be Constructive',
      description: 'Provide helpful feedback and suggestions'
    },
    {
      icon: '📚',
      title: 'Learn Together',
      description: 'Help others learn and grow in their development journey'
    },
    {
      icon: '🎯',
      title: 'Stay Focused',
      description: 'Keep discussions relevant to React Nest development'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-gradient-to-br from-purple-600/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 -right-40 w-96 h-96 bg-gradient-to-bl from-blue-600/20 to-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute -bottom-40 left-1/4 w-72 h-72 bg-gradient-to-tr from-pink-500/20 to-purple-600/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      {/* Navigation */}
      <nav className="relative z-50 bg-black/40 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">
                <img src={logo} alt="React Nest Logo" className="w-14 h-14"/>
              </span>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                React Nest
              </span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
                <Link to="/" className="text-gray-300 hover:text-white transition-colors duration-300 hover:scale-105 transform">Home</Link>
              <a href="#features" className="text-gray-300 hover:text-white transition-colors duration-300 hover:scale-105 transform">Features</a>
              <a href="#download" className="text-gray-300 hover:text-white transition-colors duration-300 hover:scale-105 transform">Download</a>
              <a href="#github" className="text-gray-300 hover:text-white transition-colors duration-300 hover:scale-105 transform">GitHub</a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-black/90 backdrop-blur-xl border-t border-white/10">
            <div className="px-6 py-4 space-y-4">
              <a href="#home" className="block text-gray-300 hover:text-white transition-colors">Home</a>
              <a href="#features" className="block text-gray-300 hover:text-white transition-colors">Features</a>
              <a href="#download" className="block text-gray-300 hover:text-white transition-colors">Download</a>
              <a href="#github" className="block text-gray-300 hover:text-white transition-colors">GitHub</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="space-y-4">
            <h1 className="text-5xl lg:text-6xl font-black leading-tight">
              Join the
              <span className="block bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                Revolution
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Help shape the future of React development. Every contribution, big or small, makes React Nest better for developers worldwide.
            </p>
          </div>

          <div className="flex items-center justify-center space-x-8 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">50+</div>
              <div className="text-gray-500">Contributors</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">200+</div>
              <div className="text-gray-500">Pull Requests</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">24/7</div>
              <div className="text-gray-500">Community</div>
            </div>
          </div>
        </div>
      </section>

      {/* Ways to Contribute */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold">
              Ways to 
              <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">Contribute</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Find the perfect way to contribute based on your skills and interests
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {contributionTypes.map((type, index) => (
              <div 
                key={index} 
                className="group p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl hover:bg-white/10 transition-all duration-500 hover:scale-105 hover:border-purple-500/50"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-4xl group-hover:scale-110 transition-transform duration-300">
                      {type.icon}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      type.difficulty === 'Beginner' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-orange-500/20 text-orange-400'
                    }`}>
                      {type.difficulty}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-white">{type.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{type.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {type.skills.map((skill, skillIndex) => (
                      <span 
                        key={skillIndex}
                        className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-lg text-sm"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Getting Started Steps */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-600/10 to-pink-500/10"></div>
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold">
              Getting
              <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent"> Started</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Follow these simple steps to make your first contribution
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {gettingStartedSteps.map((step, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300"
              >
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 rounded-xl flex items-center justify-center font-bold text-lg">
                      {step.step}
                    </div>
                    <h3 className="text-lg font-bold text-white">{step.title}</h3>
                  </div>
                  <p className="text-gray-400">{step.description}</p>
                  <div className="bg-gray-900/50 rounded-lg p-3 border border-white/10">
                    <code className="text-purple-300 text-sm">{step.command}</code>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Guidelines */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold">
              Community
              <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent"> Guidelines</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Our community thrives on mutual respect and collaboration
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {communityGuidelines.map((guideline, index) => (
              <div 
                key={index}
                className="text-center p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:bg-white/10 transition-all duration-300 hover:scale-105"
              >
                <div className="text-4xl mb-4">{guideline.icon}</div>
                <h3 className="text-lg font-bold text-white mb-2">{guideline.title}</h3>
                <p className="text-gray-400 text-sm">{guideline.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contribution Workflow */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-4xl font-bold">
                  <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">Contribution</span> Workflow
                </h2>
                <p className="text-xl text-gray-400 leading-relaxed">
                  Our streamlined process ensures your contributions are reviewed quickly and efficiently.
                </p>
                
                <div className="space-y-4">
                  {[
                    'Browse issues labeled "good first issue"',
                    'Comment on the issue to claim it',
                    'Fork and create your feature branch',
                    'Write tests and follow coding standards',
                    'Submit PR with detailed description',
                    'Collaborate with maintainers for review'
                  ].map((step, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      <span className="text-gray-300">{step}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Code Example */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl blur-xl"></div>
                <div className="relative bg-gray-900/90 backdrop-blur-sm border border-white/20 rounded-2xl overflow-hidden">
                  <div className="bg-gray-800 px-6 py-4 flex items-center space-x-3">
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <span className="text-gray-300 text-sm">Contribution Example</span>
                  </div>
                  {/* <div className="p-6 font-mono text-sm space-y-2">
                    <div className="text-gray-400">// Fix: Add package search validation</div>
                    <div className="text-blue-400">const validatePackageName = (name) => {</div>
                    <div className="text-gray-300 ml-4">if (!name || name.trim() === '') {</div>
                    <div className="text-gray-300 ml-8">throw new Error('Package name required');</div>
                    <div className="text-gray-300 ml-4">}</div>
                    <div className="text-gray-300 ml-4">return name.toLowerCase();</div>
                    <div className="text-blue-400">};</div>
                    <div className="text-gray-400">// Tests: 100% coverage ✅</div>
                    <div className="text-green-400">// PR Ready! 🚀</div>
                  </div> */}
                  <pre className="p-6 font-mono text-sm bg-gray-900 rounded-lg text-gray-300 overflow-x-auto">
                        <code>
                            {`// Fix: Add package search validation
                        const validatePackageName = (name) => {
                        if (!name || name.trim() === '') {
                            throw new Error('Package name required');
                        }
                        return name.toLowerCase();
                        };

                        // Tests: 100% coverage ✅
                        // PR Ready! 🚀`}
                        </code>
                     </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recognition & Rewards */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold">
            Recognition &
            <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent"> Rewards</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            We believe in celebrating every contribution to our community
          </p>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {[
              {
                icon: '🏆',
                title: 'Contributor Badge',
                description: 'Official recognition on our contributors page'
              },
              {
                icon: '📜',
                title: 'Certificate',
                description: 'Digital certificate for your portfolio'
              },
              {
                icon: '🎁',
                title: 'Swag & Perks',
                description: 'Exclusive React Nest merchandise for top contributors'
              }
            ].map((reward, index) => (
              <div 
                key={index}
                className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:bg-white/10 transition-all duration-300 hover:scale-105"
              >
                <div className="text-4xl mb-4">{reward.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2">{reward.title}</h3>
                <p className="text-gray-400">{reward.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="relative py-20 px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-600/10 to-pink-500/10"></div>
        <div className="relative max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold">
            Ready to Make Your
            <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent"> Impact</span>?
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Join our vibrant community of developers building the future of React tooling
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 rounded-2xl font-semibold text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/25">
              <span className="flex items-center justify-center space-x-3">
                <span>🚀</span>
                <span>Start Contributing</span>
              </span>
            </button>          
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">24/7</div>
              <div className="text-gray-400 text-sm">Community Support</div>
            </div>
            <div>
              <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">48h</div>
              <div className="text-gray-400 text-sm">Review Time</div>
            </div>
            <div>
              <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">50+</div>
              <div className="text-gray-400 text-sm">Active Contributors</div>
            </div>
            <div>
              <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">∞</div>
              <div className="text-gray-400 text-sm">Growth Opportunities</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-white/10 py-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">
                <img src={logo} alt="React Nest Logo" className="w-14 h-14"/>
              </span>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                React Nest
              </span>
            </div>
            
            <div className="flex space-x-8 text-gray-400">
              <Link to="/privacy" className="hover:text-white transition-colors">
                Privacy
                </Link>
                <Link to="/terms" className="hover:text-white transition-colors">
                Terms
                </Link>
                 <Link to="/contribute" className="hover:text-white transition-colors">
                    Contribute
                </Link>
                <Link to="/contact" className="hover:text-white transition-colors">
                 Contact
                </Link>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-gray-400">
            <p>&copy; 2025 React Nest. Built with ❤️ by contributors worldwide</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContributePage;